import { useEffect, useRef, useState } from 'react'
import lottie from 'lottie-web/build/player/lottie_light_canvas'

export default function GiftAnimation({
  lottieUrl,
  animationUrl,
  className = 'h-32',
  lazy = false,
  loop = true,
  onLoadError,
}) {
  const sourceUrl = lottieUrl || animationUrl
  const rootRef = useRef(null)
  const containerRef = useRef(null)
  const [isVisible, setIsVisible] = useState(!lazy)

  useEffect(() => {
    if (!lazy || isVisible || !rootRef.current) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '120px', threshold: 0.01 },
    )

    observer.observe(rootRef.current)
    return () => observer.disconnect()
  }, [lazy, isVisible])

  useEffect(() => {
    if (!isVisible || !containerRef.current || !sourceUrl) return undefined

    let animation = null
    let cancelled = false
    let resizeObserver = null
    let resizeFrame = 0
    let lastWidth = 0
    let lastHeight = 0

    fetch(sourceUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      })
      .then((data) => {
        if (cancelled || !containerRef.current) return

        const animationData = { ...data }
        delete animationData.tgs

        animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'canvas',
          loop,
          autoplay: true,
          animationData,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
          },
        })

        // Canvas-рендерер замеряет контейнер один раз при инициализации.
        // Если на первом заходе ширина ещё не разложена, анимация сужается.
        // ResizeObserver пересчитывает размер, как только контейнер примет финальную ширину.
        resizeObserver = new ResizeObserver(([entry]) => {
          const width = Math.round(entry?.contentRect?.width || 0)
          const height = Math.round(entry?.contentRect?.height || 0)
          if (!width || !height || (width === lastWidth && height === lastHeight)) return
          lastWidth = width
          lastHeight = height
          // ResizeObserver can fire while layout is still being committed.
          // Defer the canvas resize to the next frame and coalesce duplicates.
          if (!resizeFrame) {
            resizeFrame = requestAnimationFrame(() => {
              resizeFrame = 0
              animation?.resize()
            })
          }
        })
        resizeObserver.observe(containerRef.current)
      })
      .catch(() => {
        if (!cancelled && typeof onLoadError === 'function') onLoadError()
      })

    return () => {
      cancelled = true
      resizeObserver?.disconnect()
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      try {
        animation?.destroy()
      } catch (error) {
        // Lottie may have already detached its canvas during a WebView DOM update.
        if (error?.name !== 'NotFoundError') throw error
      }
    }
  }, [isVisible, sourceUrl, loop, onLoadError])

  return (
    <div
      ref={rootRef}
      className={`mx-auto flex w-full items-center justify-center ${className}`}
      aria-hidden="true"
    >
      {!isVisible ? (
        <div className="h-full min-h-[8rem] w-full animate-pulse rounded-2xl bg-[var(--color-card-elevated)]" />
      ) : (
        <div ref={containerRef} className="h-full w-full" />
      )}
    </div>
  )
}
